import { Bar, Position, Size } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { highlight } from 'utils/stringUtils';
import LazyLoadPicker, { ILazyLoadPickerProps } from '../../LazyLoadPicker/LazyLoadPicker';

export interface IBasicPickerValue<K extends string = string> {
  id: K;
  name: string;
}

export interface IBasicPickerProps<K extends string = string, T extends IBasicPickerValue = IBasicPickerValue<K>>
  extends Pick<ILazyLoadPickerProps<T>, 'dropDownButtonRender' | 'closeButtonRender' | 'searchValue' | 'onSearch'> {
  value: K | null;
  data: T[];
  onSelect: (value: K | null) => void;
  minWidth?: string;
  placeholder?: string;
  position?: Position;
  clearable?: boolean;
  error?: boolean;
  className?: string | object;
  disabled?: boolean;
  autofocus?: boolean;
}

const BasicPicker = <K extends string, T extends IBasicPickerValue<K>>({
  value,
  data,
  onSelect,
  searchValue,
  className,
  ...pickerProps
}: IBasicPickerProps<K, T>) => {
  function handleSelect(model: T | null) {
    onSelect(model?.id ?? null);
  }

  function renderValueTemplate(id: string | null) {
    if (id) {
      const item = data.find((model) => model.id === id);
      if (item) {
        return <div className="ellipsis">{item.name}</div>;
      }
    }

    return <></>;
  }

  function renderRow(item: T) {
    return (
      <Bar size={Size.SMALL} className="px-3">
        <div className="ellipsis">{searchValue ? highlight(item.name, searchValue) : item.name}</div>
      </Bar>
    );
  }

  const classes = classNames('basic-picker', className);

  return (
    <LazyLoadPicker
      {...pickerProps}
      searchValue={searchValue}
      className={classes}
      value={value}
      onSelect={handleSelect}
      loadedAll={true}
      offset={data.length}
      data={data}
      renderRow={renderRow}
      valueTemplate={renderValueTemplate}
    />
  );
};

export default BasicPicker;
